import { useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { cx } from "class-variance-authority";
import { motion, useAnimationControls, useMotionValue } from "motion/react";
import type { PanInfo } from "motion/react";
import { Trash } from "../Icon/Trash";
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from "../hooks/useMediaQuery";

import * as styles from "./SwipeToDelete.module.scss";

// Keep in sync with $reveal-width in SwipeToDelete.module.scss
const REVEAL_WIDTH = 72;
const COMMIT_RATIO = 0.8;
const COMMIT_VELOCITY = -1000;

type SwipeToDeleteProps = {
  children: ReactNode;
  /**
   * Called when the user commits the delete, before the row is removed. Return
   * false, or a promise resolving to false, e.g. from a dismissed confirmation
   * dialog, to cancel the delete and snap the row back into place. Any other
   * value lets the removal animation play.
   */
  onDelete: () => void | boolean | Promise<void | boolean>;
  disabled?: boolean;
  className?: string;
  /**
   * Corner radius for the row (any CSS length, e.g. "var(--radius-10)"). The
   * delete action behind the row is rounded slightly more so its red never
   * bleeds through the row's anti-aliased corners.
   */
  radius?: string;
  deleteLabel?: string;
};

export const SwipeToDelete = ({
  children,
  onDelete,
  disabled = false,
  className,
  radius,
  deleteLabel = "Delete",
}: SwipeToDeleteProps) => {
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);

  const x = useMotionValue(0);
  const controls = useAnimationControls();

  const [isDeleting, setIsDeleting] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const isCommittingRef = useRef(false);

  const getContainerWidth = () => {
    return containerRef.current?.offsetWidth ?? 0;
  };

  const commitDelete = async () => {
    if (isDeleting || isCommittingRef.current) return;

    isCommittingRef.current = true;

    // Keep the row in place with the delete action revealed while onDelete
    // resolves, e.g. awaiting a confirmation dialog. The row is only removed
    // once onDelete has run and hasn't cancelled the delete.
    controls.start({ x: -REVEAL_WIDTH });

    let confirmed;
    try {
      confirmed = await onDelete();
    } catch {
      confirmed = false;
    }

    isCommittingRef.current = false;

    if (confirmed === false) {
      controls.start({ x: 0 });
      return;
    }

    setIsDeleting(true);
  };

  const handleDragEnd = (_event: unknown, info: PanInfo) => {
    const width = getContainerWidth();
    const position = x.get();
    const pastCommitPoint =
      width > 0 &&
      (position <= -width * COMMIT_RATIO ||
        (info.velocity.x <= COMMIT_VELOCITY && position <= -REVEAL_WIDTH));

    if (pastCommitPoint) {
      commitDelete();
    } else if (position <= -REVEAL_WIDTH / 2) {
      controls.start({ x: -REVEAL_WIDTH });
    } else {
      controls.start({ x: 0 });
    }
  };

  const handleContentClick = () => {
    if (!isDeleting && !isCommittingRef.current && x.get() < 0) {
      controls.start({ x: 0 });
    }
  };

  const radiusStyle = radius
    ? ({ "--swipe-radius": radius } as CSSProperties)
    : undefined;

  if (isDesktop) {
    return (
      <div className={cx(styles.container, className)} style={radiusStyle}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      style={radiusStyle}
      className={cx(styles.container, className)}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.2, ease: "easeIn" }}
    >
      <button
        type="button"
        className={styles.deleteAction}
        aria-label={deleteLabel}
        tabIndex={isDeleting ? -1 : 0}
        onClick={commitDelete}
      >
        <span className={styles.deleteIcon}>
          <Trash />
        </span>
      </button>

      <motion.div
        className={styles.content}
        style={{ x }}
        animate={controls}
        drag={disabled || isDeleting ? false : "x"}
        dragConstraints={{ right: 0 }}
        dragElastic={0.1}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
        onClick={handleContentClick}
      >
        {children}
      </motion.div>
    </motion.div>
  );
};
